import React from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import { setConsent } from "../cookieConsentSlice";
import { openGlobalModal } from "../../globalModal/globalModalSlice";
import { DEFAULT_CONSENT } from "../types";

const CookieBanner: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // We only show this banner if the user has NOT consented yet (isBannerOpen is true)
  const { isBannerOpen, hasUserConsented } = useAppSelector(
    (state) => state.cookieConsent
  );

  // Get localized text from PHP
  const bannerText = window.commonVariables?.cookieConsent.bannerText;
  const policyLink = window.commonVariables?.cookieConsent.policyLink;

  if (!isBannerOpen && hasUserConsented) {
    return null;
  }

  const handleAcceptAll = () => {
    const timestamp = new Date().toISOString();
    dispatch(
      setConsent({
        ...DEFAULT_CONSENT,
        analytics: true,
        marketing: true,
        timestamp,
      })
    );
  };

  /**
   * REJECT ALL LOGIC:
   * 1. We start with DEFAULT_CONSENT (where analytics/marketing are FALSE).
   * 2. We dispatch it.
   * 3. The ConsentManager receives 'false', so it DOES NOT activate the scripts.
   * 4. The scripts remain <script type="text/plain"> (Blocked).
   * 5. The choice is saved to the cookie so the banner doesn't appear again.
   */
  const handleRejectAll = () => {
    const timestamp = new Date().toISOString();
    dispatch(
      setConsent({
        ...DEFAULT_CONSENT,
        // Explicitly ensuring these are false
        analytics: false,
        marketing: false,
        timestamp,
      })
    );
  };

  const handleOpenPreferences = () => {
    dispatch(
      openGlobalModal({
        contentKey: "cookiePreferences",
        title: t("cookieConsent.preferencesTitle", "Cookie Preferences"),
      })
    );
  };

  if (!isBannerOpen) return null;

  return (
    <div className="cookie-banner" role="dialog" aria-live="polite">
      <div className="cookie-banner__container">
        <div className="cookie-banner__content">
          <p className="cookie-banner__text">
            {bannerText}
            {policyLink && (
              <a href={policyLink} className="cookie-banner__link">
                {t("cookieConsent.readMore", "Read More")}
              </a>
            )}
          </p>
        </div>
        <div className="cookie-banner__actions">
          <button
            className="cookie-banner__btn cookie-banner__btn--primary"
            onClick={handleAcceptAll}
          >
            {t("cookieConsent.acceptAll", "Accept All")}
          </button>
          <button
            className="cookie-banner__btn cookie-banner__btn--primary"
            onClick={handleRejectAll}
          >
            {t("cookieConsent.rejectAll", "Reject All")}
          </button>

          <button
            className="cookie-banner__btn cookie-banner__btn--secondary"
            onClick={handleOpenPreferences}
          >
            {t("cookieConsent.preferences", "Preferences")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
